import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import api from '../utils/api';
import AuthShell, { AuthHeading, FormAlert } from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';

export default function ForgotPassword() {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [fieldErr, setFieldErr] = useState(null);
  const [formErr, setFormErr]   = useState(null);
  const emailRef = useRef(null);

  const handleSubmit = async e => {
    e.preventDefault();
    setFormErr(null);
    if (!email.trim()) {
      setFieldErr('Enter the email address you sign in with.');
      emailRef.current?.focus();
      return;
    }
    setFieldErr(null);
    setLoading(true);
    try {
      // Endpoint is anti-enumeration: it always returns 200. Show the same
      // calm confirmation regardless of whether the email exists.
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      // Even on an unexpected error, avoid revealing account existence.
      // Only surface a generic problem if the request truly failed.
      if (err.response) {
        setSent(true);
      } else {
        setFormErr("We couldn't reach Rostery. Check your connection, then try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {!sent ? (
        /* ── Request step ── */
        <div>
          <AuthHeading title="Forgot your password?">
            Enter the email address you sign in with and we'll send you a link to choose a new password.
          </AuthHeading>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <AuthField
              ref={emailRef}
              label="Email address"
              icon={Mail}
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (fieldErr) setFieldErr(null); }}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              error={fieldErr}
            />

            {formErr && <FormAlert>{formErr}</FormAlert>}

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-sm">
              {loading
                ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Sending…</>
                : <>Send reset link <ArrowRight size={15} aria-hidden="true" /></>}
            </button>
          </form>

          <Link to="/login"
            className="flex items-center justify-center gap-1.5 min-h-[40px] text-sm text-slate-600 hover:text-slate-900 mt-5">
            <ArrowLeft size={15} aria-hidden="true" /> Back to sign in
          </Link>
        </div>
      ) : (
        /* ── Confirmation step (anti-enumeration) ── */
        <div role="status">
          <span className="w-11 h-11 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center mb-5" aria-hidden="true">
            <MailCheck size={20} />
          </span>
          <AuthHeading title="Check your inbox">
            If an account uses <strong className="font-semibold text-slate-700">{email}</strong>, we've sent it a link
            to choose a new password. The link expires soon, so use it shortly.
          </AuthHeading>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => { setSent(false); setEmail(''); }}
              className="btn-secondary w-full py-2.5 text-sm"
            >
              Use a different email
            </button>
            <Link to="/login"
              className="flex items-center justify-center gap-1.5 min-h-[40px] text-sm text-slate-600 hover:text-slate-900">
              <ArrowLeft size={15} aria-hidden="true" /> Back to sign in
            </Link>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
